<template>
  <ReportCard :title="$t('report.avg_ticket_title')" header-margin="mb-4">
    <div class="flex flex-col gap-3">
      <div v-for="type in allClientTypeKeys" :key="'avg-' + type"
        class="flex items-center justify-between">
        <!-- Left: color dot + type name -->
        <div class="flex items-center gap-2 min-w-0">
          <span class="w-2.5 h-2.5 rounded-full shrink-0"
            :style="{ backgroundColor: clientTypeColor(type) }"></span>
          <span class="text-[13px] font-semibold text-foreground">{{ type }}</span>
        </div>

        <!-- Right: current amount + evolution pill + previous -->
        <div class="flex items-center gap-2">
          <span class="text-[15px] font-bold text-foreground">
            {{ avgTicketForType(type).currentFormatted }}
          </span>
          <EvolutionIndicator v-if="avgTicketForType(type).hasCurrentData"
            :value="avgTicketForType(type).evolution"
            :formatted="avgTicketForType(type).evolutionFormatted"
            :show-neutral="true" />
          <span class="text-[11px] text-muted-foreground">
            {{ avgTicketForType(type).previousFormatted }}
          </span>
        </div>
      </div>
    </div>
  </ReportCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { SalesByClientTypesResponse, TaxDataKey } from '../../responses'
import { useAppSettingsStore } from '../../stores/AppSettingsStore'
import { formatCurrencyForCenter } from '../../utils/Languages'
import { clientTypeColor } from '../../utils/ClientTypeColors'
import ReportCard from '../ReportCard.vue'
import EvolutionIndicator from '../ui/EvolutionIndicator.vue'

const props = defineProps<{
  response: SalesByClientTypesResponse
  taxKey: TaxDataKey
  centerId: number
}>()

const appSettingsStore = useAppSettingsStore()
const centerObj = computed(() => appSettingsStore.getCenter(props.centerId))

/** Types with current OR previous data */
const allClientTypeKeys = computed<string[]>(() => {
  if (!props.response?.client_types) return []
  return Object.keys(props.response.client_types).filter(type => {
    const data = props.response.client_types[type]
    if (!data?.summary) return false
    const cur = data.summary.total_period.tax_data
    const prev = data.summary.total_previous_period?.tax_data
    return (cur.excluded_tax !== 0 || cur.included_tax !== 0)
      || (prev && (prev.excluded_tax !== 0 || prev.included_tax !== 0))
  })
})

interface AvgTicketResult {
  currentFormatted: string
  previousFormatted: string
  evolution: number
  evolutionFormatted: string
  hasCurrentData: boolean
}

function avgTicketForType(type: string): AvgTicketResult {
  const data = props.response?.client_types[type]
  if (!data?.summary) {
    return { currentFormatted: '-', previousFormatted: '-', evolution: 0, evolutionFormatted: '0 %', hasCurrentData: false }
  }
  const center = centerObj.value
  const curRevenue = data.summary.total_period.tax_data[props.taxKey]
  const curDocs = data.summary.total_period.total_documents
  const currentAvg = curDocs > 0 ? curRevenue / curDocs : 0

  const prevRevenue = data.summary.total_previous_period?.tax_data[props.taxKey] ?? 0
  const prevDocs = data.summary.total_previous_period?.total_documents ?? 0
  const previousAvg = prevDocs > 0 ? prevRevenue / prevDocs : 0

  const evolution = previousAvg > 0 ? ((currentAvg - previousAvg) / previousAvg) * 100 : 0

  const fmtCurrent = curDocs > 0 ? formatCurrencyForCenter(currentAvg, center) : '-'
  const fmtPrevious = prevDocs > 0 ? formatCurrencyForCenter(previousAvg, center) : '-'

  const sign = evolution > 0 ? '+' : ''
  const evoFmt = previousAvg > 0 && curDocs > 0
    ? `${sign}${evolution.toFixed(1)} %`
    : '0 %'

  return {
    currentFormatted: fmtCurrent,
    previousFormatted: fmtPrevious,
    evolution,
    evolutionFormatted: evoFmt,
    hasCurrentData: curDocs > 0,
  }
}
</script>
